import {
  useActiveCamera,
  type CameraSwitchEvent,
} from "@/components/r3f/cameras/active-camera-context";
import { PHOTO_SIZE } from "@/object-cache";
import { useAppStore } from "@/store/store-hooks";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import { CameraViewType } from "@/utils/cam-view";
import {
  getSheetSize,
  selectIElementWorldTransform,
  useNonExhaustiveEffect,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { isIElementImg2d } from "@faro-lotv/ielement-types";
import { useCallback, useLayoutEffect, useRef } from "react";
import type { FlyWalkAnimation } from "./data-dispatch/flywalk-animation";
import type { FlyWalkSceneActiveElement } from "./flywalk-types";
import type { SingleViewCameras } from "./use-fly-split-scene-cameras";

/**
 * This hook enables the caller component to use an ortho camera when the flywalk mode is rendering a 2D image or an orthomosaic,
 * It should be kept in sync with the complementary hook 'usePerspectiveCameraForNonImages' that sets the camera projection to
 * perspective when the main element is not a 2D image or an orthomosaic.
 *
 * @param main The current main element in the flywalk scene
 * @param flyWalkAnimation The current flywalk animation
 * @param fromDeepLink Whether the 2D image is set from a deep link
 */
export function useOrthoCameraForImages(
  main: FlyWalkSceneActiveElement | null,
  flyWalkAnimation: FlyWalkAnimation | null,
  fromDeepLink: boolean,
): void {
  const { cameraProjection, setCameraProjection, onPerspectiveToOrtho } =
    useActiveCamera();

  const switchToOrtho = useCallback(() => {
    setCameraProjection(CameraViewType.orthographic);
  }, [setCameraProjection]);

  useOrthoCameraForImagesImpl(
    main,
    flyWalkAnimation,
    fromDeepLink,
    cameraProjection,
    switchToOrtho,
  );

  const { getState } = useAppStore();

  const toOrtho = useCallback(
    (switchEvent: CameraSwitchEvent) => {
      if (!main) return;
      if (isIElementImg2d(main)) {
        // Setup the incoming ortho camera with the correct vertical size to match the photo height
        const pose = selectIElementWorldTransform(main.id)(getState());
        const maxPixelSize = Math.max(main.pixelWidth, main.pixelHeight);
        const halfHeight =
          (PHOTO_SIZE * pose.scale[0] * 0.5 * main.pixelHeight) / maxPixelSize;
        switchEvent.orthoCamera.top = halfHeight;
        switchEvent.orthoCamera.bottom = -halfHeight;
        switchEvent.orthoCamera.zoom = 1.0;
      } else if (isIElementOrthomosaic(main)) {
        // Setup the incoming ortho camera with the correct vertical size to match the orthomosaic image height
        const pose = selectIElementWorldTransform(main.id)(getState());
        const sheetSize = getSheetSize(main);
        const halfHeight = sheetSize.height * 0.5 * pose.scale[0];
        switchEvent.orthoCamera.top = halfHeight;
        switchEvent.orthoCamera.bottom = -halfHeight;
        switchEvent.orthoCamera.zoom = 1.0;
      }
    },
    [main, getState],
  );

  useTypedEvent(onPerspectiveToOrtho, toOrtho);

  // Ensure that, when this hook is unmounted, the camera projection is restored to perspective
  useNonExhaustiveEffect(
    () => () => {
      setCameraProjection(CameraViewType.perspective);
    },
    [],
  );
}

function useOrthoCameraForImagesImpl(
  main: FlyWalkSceneActiveElement | null,
  flyWalkAnimation: FlyWalkAnimation | null,
  fromDeepLink: boolean,
  cameraProjection: CameraViewType,
  switchToOrtho: () => void,
): void {
  // The only temporal fact we can't derive from current props: whether an animation
  // toward the current image was in progress, so we can detect the moment it ends.
  // A ref keeps this out of the render loop.
  const wasAnimatingToImage = useRef(false);

  useLayoutEffect(() => {
    // When the mode is being initialized and there is no main object, do nothing.
    // It could be that we need a perspective projection, but it could also be that
    // a deep link to a photo wants an ortho projection.
    if (main === null) return;

    const isImage = isIElementImg2d(main) || isIElementOrthomosaic(main);
    if (!isImage) {
      // Perspective is restored by usePerspectiveCameraForNonImages.
      wasAnimatingToImage.current = false;
      return;
    }

    if (flyWalkAnimation) {
      // Still animating toward the image; only switch once it finishes.
      wasAnimatingToImage.current = true;
      return;
    }

    // Image is now shown without animation: either the animation just finished,
    // or it was set directly via a deep link.
    const arrivedAtImage = wasAnimatingToImage.current || fromDeepLink;
    wasAnimatingToImage.current = false;
    if (arrivedAtImage && cameraProjection !== CameraViewType.orthographic) {
      switchToOrtho();
    }
  }, [main, flyWalkAnimation, fromDeepLink, cameraProjection, switchToOrtho]);
}

/**
 * Ensures that the camera projection is set to orthographic when the main element in the split view is a 2D image or an orthomosaic,
 * right after the animation that introduced it.
 *
 * @param main The current main element in this split view
 * @param flyWalkAnimation The current flywalk animation in this split view
 * @param fromDeepLink Whether the 2d image is set from a deep link
 * @param cameras The cameras used in this split view
 */
export function useOrthoCameraForImagesSplitView(
  main: FlyWalkSceneActiveElement | null,
  flyWalkAnimation: FlyWalkAnimation | null,
  fromDeepLink: boolean,
  cameras: SingleViewCameras,
): void {
  const { getState } = useAppStore();

  const { projection, changeProjection } = cameras;

  const switchToOrtho = useCallback(() => {
    if (!main) return;
    let cameraHeight: number | undefined;
    if (isIElementImg2d(main)) {
      // Setup the incoming ortho camera with the correct vertical size to match the photo height
      const pose = selectIElementWorldTransform(main.id)(getState());
      const maxPixelSize = Math.max(main.pixelWidth, main.pixelHeight);
      cameraHeight =
        (PHOTO_SIZE * pose.scale[0] * main.pixelHeight) / maxPixelSize;
    } else if (isIElementOrthomosaic(main)) {
      // Setup the incoming ortho camera with the correct vertical size to match the orthomosaic image height
      const pose = selectIElementWorldTransform(main.id)(getState());
      const sheetSize = getSheetSize(main);
      cameraHeight = sheetSize.height * pose.scale[0];
    }
    changeProjection(CameraViewType.orthographic, cameraHeight);
  }, [changeProjection, getState, main]);

  useOrthoCameraForImagesImpl(
    main,
    flyWalkAnimation,
    fromDeepLink,
    projection,
    switchToOrtho,
  );

  // Ensure that, when this hook is unmounted, the camera projection is restored to perspective
  useNonExhaustiveEffect(
    () => () => {
      changeProjection(CameraViewType.perspective);
    },
    [],
  );
}

/**
 * Before the animation even starts, ensures that the camera projection is set to perspective
 * when the main element is not a 2D image or an orthomosaic.
 *
 * @param main The current main element in the flywalk scene
 */
export function usePerspectiveCameraForNonImages(
  main: FlyWalkSceneActiveElement | null,
): void {
  const { cameraProjection, setCameraProjection } = useActiveCamera();
  usePerspectiveCameraForNonImagesImpl(
    main,
    cameraProjection,
    setCameraProjection,
  );
}

function usePerspectiveCameraForNonImagesImpl(
  main: FlyWalkSceneActiveElement | null,
  cameraProjection: CameraViewType,
  setCameraProjection: (projection: CameraViewType) => void,
): void {
  useLayoutEffect(() => {
    if (!main) return;
    const isImage = isIElementImg2d(main) || isIElementOrthomosaic(main);
    if (!isImage && cameraProjection !== CameraViewType.perspective) {
      setCameraProjection(CameraViewType.perspective);
    }
  }, [main, cameraProjection, setCameraProjection]);
}

/**
 * Ensures that the camera projection is perspective when the main element in the split view is not a 2D image or an orthomosaic.
 *
 * @param main Main object in the given split view
 * @param cameras Cameras used in the given split view
 */
export function usePerspectiveCameraForNonImagesSplitView(
  main: FlyWalkSceneActiveElement | null,
  cameras: SingleViewCameras,
): void {
  usePerspectiveCameraForNonImagesImpl(
    main,
    cameras.projection,
    cameras.changeProjection,
  );
}
